import type { UserReference } from '@core/account-types';
import type { Attachment } from '@core/attachments-types';
import type {
  Note,
  NoteId,
  NotePayload,
  NoteSave,
  NoteUploadAttachmentEvent,
} from '@core/notes-types';

export interface EditableNotesListState {
  notes: {
    data: Note[] | null;
  };

  noteAttachmentsBeingUploaded: {
    // note id to beingUploaded
    [key: string]: string[];
  };

  noteAttachmentsTemp: {
    // note id to Attachments
    [key: string]: Array<{ uuid: string; attachment: Attachment }>;
  };

  notesBeingSaved: NoteId[];
  notesBeingDeleted: NoteId[];
  notesBeingEdited: NoteId[];
  notesWithError: NoteId[];
  notesBeingEditedCache: Note[];
  notesBeingRepliedTo: NoteId[];
}

export type CurrentlyTypedNotes = Array<{
  admin: UserReference;
  replyToId?: number;
}>;

export function handleNoteSaveStart(
  state: EditableNotesListState,
  payload: { note: NoteSave<Note>['note'] },
): any {
  return {
    ...state,
    notesBeingSaved: [...state.notesBeingSaved, payload.note.id || payload.note.replyToId],
  };
}

export function handleNoteSaveSuccess(
  state: EditableNotesListState,
  payload: { id?: number | null; replyToId?: number | null; note: NotePayload },
): any {
  return {
    ...state,
    // NOTE: temporary id (UUID) is overwritten with id after creating the note,
    // so we cannot properly filter the array. We can assume that
    // the actions will be performed and will end synchronously.
    // The only ID that allows us to track "notesBeingSaved" is correlationID.
    notesBeingSaved: state.notesBeingSaved.slice(0, -1),
    notesBeingEdited: state.notesBeingEdited.filter(id => id !== payload.note.id),
    notesBeingRepliedTo: state.notesBeingRepliedTo.filter(id => id !== payload.note.replyToId),
    noteAttachmentsTemp: {
      ...state.noteAttachmentsTemp,
      [payload.note.id || 'null']: [],
    },
    notesWithError: state.notesWithError.filter(id => id !== payload.note.id),
  };
}

export function handleNoteUpdateFailure(
  state: EditableNotesListState,
  payload: { id: number | 'NEW_CANDIDATE_NOTE' | null },
): any {
  return {
    ...state,
    notesBeingSaved: state.notesBeingSaved.slice(0, -1),
    notesWithError: [...state.notesWithError, payload.id!],
  };
}

export function handleNoteSaveFailure(state: EditableNotesListState, payload: { id: NoteId }): any {
  return {
    ...state,
    notesBeingSaved: state.notesBeingSaved.slice(0, -1),
    notesWithError: [...state.notesWithError, payload.id!],
  };
}

export function handleNoteAttachmentDeleteSuccess(
  state: EditableNotesListState,
  payload: { note?: Note; attachmentId: number },
): any {
  const deletedAttachmentNoteId = (payload.note && payload.note.id) || null;

  return {
    ...state,
    noteAttachmentsTemp: {
      ...state.noteAttachmentsTemp,
      // @ts-ignore
      [deletedAttachmentNoteId]: (state.noteAttachmentsTemp[deletedAttachmentNoteId] || []).filter(
        ({ attachment }: { attachment: { id: number } }) => attachment.id !== payload.attachmentId,
      ),
    },
    notes: {
      ...state.notes,
      data: state.notes.data!.map(_note => recursiveDeleteAttachments(_note, payload.attachmentId)),
    },
  };
}

export function handleClearNoteAttachmentTemp(
  state: EditableNotesListState,
  payload: { note: { id?: NoteId | null; replyToId?: NoteId } },
): any {
  const nextState = { ...state };
  delete nextState.noteAttachmentsTemp[
    payload.note ? payload.note.id || payload.note.replyToId! : 'null'
  ];

  return nextState;
}

export function handleUploadNoteAttachment(
  state: EditableNotesListState,
  payload: { uuid: string; note: NoteUploadAttachmentEvent['note'] },
): any {
  const uploadingAttachmentNoteId = `${payload.note ? payload.note.id : null}`;

  return {
    ...state,
    noteAttachmentsBeingUploaded: {
      ...state.noteAttachmentsBeingUploaded,
      [uploadingAttachmentNoteId]: [
        payload.uuid,
        ...(state.noteAttachmentsBeingUploaded[uploadingAttachmentNoteId] || []),
      ],
    },
  };
}

export function handleUploadNoteAttachmentSuccess(
  state: EditableNotesListState,
  payload: { note: NoteUploadAttachmentEvent['note']; uuid: string; attachment: Attachment },
): any {
  const noteId = `${payload.note ? payload.note.id : null}`;

  return {
    ...state,
    noteAttachmentsBeingUploaded: {
      ...state.noteAttachmentsBeingUploaded,
      [noteId]: (state.noteAttachmentsBeingUploaded[noteId] || []).filter(
        (uuid: string) => uuid !== payload.uuid,
      ),
    },
    noteAttachmentsTemp: {
      ...state.noteAttachmentsTemp,
      [noteId]: [payload, ...(state.noteAttachmentsTemp[noteId] || [])],
    },
  };
}

export function handleUploadNoteAttachmentFailure(
  state: EditableNotesListState,
  payload: { note: NoteUploadAttachmentEvent['note']; uuid: string },
): any {
  const uploadedAttachmentNoteId = payload.note ? payload.note.id : null;
  return {
    ...state,
    noteAttachmentsBeingUploaded: {
      ...state.noteAttachmentsBeingUploaded,
      [uploadedAttachmentNoteId!]: (
        state.noteAttachmentsBeingUploaded[uploadedAttachmentNoteId!] || []
      ).filter(uuid => uuid !== payload.uuid),
    },
  };
}

export function handleDiscardNote(
  state: EditableNotesListState,
  payload: { id: NoteId | null } = { id: null },
): any {
  const isBeingEdited = state.notesBeingEdited.includes(payload.id!);
  const isBeingReplied = state.notesBeingRepliedTo.includes(payload.id!);

  if (isBeingEdited || isBeingReplied) {
    return {
      ...state,
      notesBeingEdited: isBeingEdited
        ? state.notesBeingEdited.filter(noteId => noteId !== payload.id)
        : state.notesBeingEdited,
      notesBeingRepliedTo: isBeingReplied
        ? state.notesBeingRepliedTo.filter(noteId => noteId !== payload.id)
        : state.notesBeingRepliedTo,
      noteAttachmentsTemp: {
        ...state.noteAttachmentsTemp,
        [payload.id || 'null']: [],
      },
      notesBeingEditedCache: isBeingEdited
        ? state.notesBeingEditedCache.filter(cachedNote => cachedNote.id !== payload.id)
        : state.notesBeingEditedCache,
      notes: {
        ...state.notes,
        data: isBeingEdited
          ? state.notes.data!.map(_note =>
              recursiveDiscardNote(_note, payload.id!, state.notesBeingEditedCache),
            )
          : state.notes.data,
      },
    };
  } else {
    return state;
  }
}

export function handleEditNote(state: EditableNotesListState, payload: Partial<Note>): any {
  return {
    ...state,
    notesBeingEdited: [<NoteId>payload.id, ...state.notesBeingEdited],
    notesBeingEditedCache: [<Note>payload, ...state.notesBeingEditedCache],
  };
}

export function handleReplyToNote(
  state: EditableNotesListState,
  payload: { id: NoteId | null },
): any {
  return {
    ...state,
    notesBeingRepliedTo: [payload.id, ...state.notesBeingRepliedTo],
  };
}

export function handleDeleteNote(state: EditableNotesListState, payload: { noteId: NoteId }): any {
  return {
    ...state,
    notesBeingDeleted: [...state.notesBeingDeleted, payload.noteId],
  };
}

export function handleDeleteNoteSuccess(
  state: EditableNotesListState,
  payload: { id: NoteId | null },
): any {
  return {
    ...state,
    notesBeingDeleted: state.notesBeingDeleted.filter(id => id !== payload.id),
  };
}

export function recursiveDeleteAttachments(note: Note, _attachmentId: number): Note {
  if (note.attachments.find(a => a.id === _attachmentId)) {
    return {
      ...note,
      attachments: note.attachments.filter(a => a.id !== _attachmentId),
    };
  } else if (note.replies) {
    return {
      ...note,
      replies: note.replies.map(n => recursiveDeleteAttachments(n, _attachmentId)),
    };
  } else {
    return note;
  }
}

export function recursiveDiscardNote(note: Note, discardedId: NoteId, cache: Note[] = []): Note {
  if (note.id === discardedId) {
    return {
      ...cache.find(cachedNote => cachedNote.id === note.id)!,
      replies: note.replies,
    };
  } else if (note.replies) {
    return {
      ...note,
      replies: note.replies.map(reply => recursiveDiscardNote(reply, discardedId, cache)),
    };
  } else {
    return note;
  }
}
